import * as React from 'react';
import { chakra } from '@chakra-ui/react';
import {
  buttonRecipe,
  iconButtonRecipe,
} from '../../theme/components/button.recipe';
import { Icon } from './Icon';

// ============================================================================
// Shared Types & Utilities
// ============================================================================

export interface IButtonProps {
  // Specific types
  add?: boolean;
  close?: boolean;
  settings?: boolean;
  // -----------------
  icon?: string;
  id?: string;
  children?: React.ReactNode;
  border?: string;
  color?: string;
  disabled?: boolean;
  flavor?: 'primary' | 'secondary' | 'accent' | 'warning' | 'danger' | 'none';
  onClick?: (e: React.MouseEvent<HTMLElement>) => void;
  outline?: boolean;
  style?: React.CSSProperties;
  type?: 'button' | 'submit' | 'reset';
  title?: string;
  'data-testid'?: string;
}

// ============================================================================
// Button - Unified button component with variant props
// ============================================================================
const HButton = chakra('button', buttonRecipe);
const HIconButton = chakra('button', iconButtonRecipe);

export const AddButton = (props: IButtonProps): React.ReactElement => {
  return (
    <HIconButton
      buttonType='add'
      id={props.id}
      role='button'
      aria-label='Add'
      aria-disabled={props.disabled}
      onClick={props.onClick}
      disabled={props.disabled}
      data-testid={props['data-testid']}
      css={{
        '& i': {
          color: 'inherit !important',
        },
      }}
    >
      <Icon name='add' />
    </HIconButton>
  );
};

export const AddMetricButton = (props: IButtonProps): React.ReactElement => {
  return (
    <HButton
      buttonType='addMetric'
      id={props.id}
      role='button'
      aria-label='add_metric'
      aria-disabled={props.disabled}
      onClick={props.onClick}
      disabled={props.disabled}
      data-testid={props['data-testid']}
      css={{
        '& i': {
          color: 'inherit !important',
        },
      }}
    >
      <Icon name='health_metrics' style={{ marginRight: '3px' }} />
      <span style={{ textTransform: 'uppercase', marginTop: '2px' }}>
        Add Metrics
      </span>
    </HButton>
  );
};

export const CloseButton = (props: IButtonProps): React.ReactElement => {
  return (
    <HIconButton
      buttonType='close'
      id={props.id}
      role='button'
      aria-label='close'
      aria-disabled={props.disabled}
      onClick={props.onClick}
      disabled={props.disabled}
      data-testid={props['data-testid']}
      css={{
        '& i': {
          color: 'inherit !important',
        },
      }}
    >
      <Icon name='close' size='tiny' />
    </HIconButton>
  );
};

export const SaveButton = (props: IButtonProps): React.ReactElement => {
  return (
    <HButton
      buttonType='save'
      id={props.id}
      type={props.type || 'submit'}
      role='button'
      aria-label='save'
      aria-disabled={props.disabled}
      onClick={props.onClick}
      disabled={props.disabled}
      data-testid={props['data-testid']}
      css={{
        '& i': {
          color: 'inherit !important',
        },
      }}
    >
      <Icon name='save' style={{ marginRight: '3px' }} />
      <span style={{ textTransform: 'uppercase', marginTop: '2px' }}>Save</span>
    </HButton>
  );
};

export const ExportButton = (props: IButtonProps): React.ReactElement => {
  return (
    <HButton
      buttonType='toolbar'
      id={props.id}
      role='button'
      aria-label='export'
      aria-disabled={props.disabled}
      onClick={props.onClick}
      disabled={props.disabled}
      data-testid={props['data-testid']}
      css={{
        '& i': {
          color: 'inherit !important',
        },
      }}
    >
      <Icon name='cloud_download' style={{ marginRight: '3px' }} />
      <span style={{ textTransform: 'uppercase', marginTop: '2px' }}>Export</span>
    </HButton>
  );
};

export const ComparisonButton = (props: IButtonProps): React.ReactElement => {
  return (
    <HButton
      buttonType='toolbar'
      id={props.id}
      role='button'
      aria-label='comparison'
      aria-disabled={props.disabled}
      onClick={props.onClick}
      disabled={props.disabled}
      data-testid={props['data-testid']}
      css={{
        '& i': {
          color: 'inherit !important',
        },
      }}
    >
      <Icon name='compare_arrows' style={{ marginRight: '3px' }} />
      <span style={{ textTransform: 'uppercase', marginTop: '2px' }}>Comparison</span>
    </HButton>
  );
};

export const SettingsButton = (props: IButtonProps): React.ReactElement => {
  return (
    <HButton
      buttonType='settings'
      id={props.id}
      role='button'
      aria-label='settings'
      aria-disabled={props.disabled}
      onClick={props.onClick}
      disabled={props.disabled}
      data-testid={props['data-testid']}
      css={{
        '& i': {
          color: 'inherit !important',
        },
      }}
    >
      <Icon name='settings' style={{ marginRight: '3px' }} />
      <span style={{ textTransform: 'uppercase', marginTop: '2px' }}>
        Settings
      </span>
    </HButton>
  );
};

export const SelectAllButton = (props: IButtonProps): React.ReactElement => {
  return (
    <HButton
      buttonType='selectAll'
      id={props.id}
      role='button'
      aria-label='select all'
      aria-disabled={props.disabled}
      onClick={props.onClick}
      disabled={props.disabled}
      style={props.style}
      data-testid={props['data-testid']}
      css={{
        '& i': {
          color: 'inherit !important',
        },
      }}
    >
      <Icon name='select_all' style={{ marginRight: '3px' }} />
      <span style={{ textTransform: 'uppercase', marginTop: '2px' }}>
        Select All
      </span>
    </HButton>
  );
};

export const ClearAllButton = (props: IButtonProps): React.ReactElement => {
  return (
    <HButton
      buttonType='clearAll'
      id={props.id}
      role='button'
      aria-label='clear all'
      aria-disabled={props.disabled}
      onClick={props.onClick}
      disabled={props.disabled}
      style={props.style}
      data-testid={props['data-testid']}
      css={{
        '& i': {
          color: 'inherit !important',
        },
      }}
    >
      <Icon name='cancel' style={{ marginRight: '3px' }} />
      <span style={{ textTransform: 'uppercase', marginTop: '2px' }}>
        Clear All
      </span>
    </HButton>
  );
};

export const Button = (props: IButtonProps): React.ReactElement => {
  return (
    <HButton
      flavor={props.flavor || 'primary'}
      onClick={props.onClick}
      disabled={props.disabled}
      id={props.id}
      type={props.type}
      style={props.style}
      role='button'
      aria-disabled={props.disabled}
      data-testid={props['data-testid']}
    >
      {props.icon && (
        <Icon
          color={props.color}
          name={props.icon}
          style={{ marginRight: '3px' }}
        />
      )}
      {props.title && (
        <span style={{ textTransform: 'uppercase', marginTop: '2px' }}>
          {props.title}
        </span>
      )}
      {props.children}
    </HButton>
  );
};

// ============================================================================
// IconButton - Button with icon and optional label
// ============================================================================

export const IconButton = (props: IButtonProps): React.ReactElement => {
  return (
    <HIconButton
      outline={props.outline}
      role='button'
      aria-label={props.title}
      title={props.title}
      aria-disabled={props.disabled}
      style={props.style}
      onClick={props.disabled ? undefined : props.onClick}
      disabled={props.disabled}
      data-testid={props['data-testid']}
    >
      <Icon color={props.color} name={props.icon} />
    </HIconButton>
  );
};
